import type * as React from 'react';
import {MOBILE_OPTION_HEIGHT, MOBILE_WHEEL_BUFFER_OPTIONS} from './constants';
import {type SelectValue} from './types';
import {type SelectOption} from '../../lib/selector-option';

export function getOptionIndex<
  T,
  V extends SelectValue,
  L extends string | number | React.ReactNode,
>(options: SelectOption<T, V, L>[], option?: SelectOption<T, V, L> | null) {
  if (!option) return -1;

  return options.findIndex(item => item.value === option.value);
}

export function getFirstEnabledOptionIndex<
  T,
  V extends SelectValue,
  L extends string | number | React.ReactNode,
>(options: SelectOption<T, V, L>[]) {
  return options.findIndex(option => !option.isDisabled);
}

export function getLastEnabledOptionIndex<
  T,
  V extends SelectValue,
  L extends string | number | React.ReactNode,
>(options: SelectOption<T, V, L>[]) {
  for (let index = options.length - 1; index >= 0; index -= 1) {
    if (!options[index]?.isDisabled) {
      return index;
    }
  }

  return -1;
}

export function getNextEnabledOptionIndex<
  T,
  V extends SelectValue,
  L extends string | number | React.ReactNode,
>(options: SelectOption<T, V, L>[], startIndex: number, step: 1 | -1) {
  let nextIndex = startIndex;

  while (nextIndex >= 0 && nextIndex < options.length) {
    if (!options[nextIndex]?.isDisabled) {
      return nextIndex;
    }

    nextIndex += step;
  }

  return -1;
}

export function getOptionScrollTop(index: number) {
  return index * MOBILE_OPTION_HEIGHT;
}

export function getWheelSpacerHeight() {
  return MOBILE_OPTION_HEIGHT * MOBILE_WHEEL_BUFFER_OPTIONS;
}

export function getMobileOptionStyles(index: number, scrollTop: number) {
  const virtualCenterIndex = scrollTop / MOBILE_OPTION_HEIGHT;
  const offsetFromCenter = index - virtualCenterIndex;
  const distance = Math.abs(offsetFromCenter);
  const normalizedDistance = Math.min(distance / 3.5, 1);
  const centerInfluence = 1 - normalizedDistance;
  const easedInfluence = 1 - normalizedDistance ** 1.65;
  const rotation = Math.max(-42, Math.min(42, offsetFromCenter * 11));
  const scale = 0.875 + easedInfluence * 0.125;
  const scaleY = 0.66 + easedInfluence * 0.34;
  const translateY = offsetFromCenter * 0.9;
  const translateZ = Math.max(0, 14 - distance * 4.5);
  const blur = (1 - easedInfluence) * 1.2;
  const opacity = 0.12 + easedInfluence * 0.88;
  const fontSize = 16 + easedInfluence * 6;
  const isSelected = distance < 0.5;
  const textShade = isSelected
    ? Math.round(164 - centerInfluence * 117)
    : Math.round(180 - easedInfluence * 60);

  return {
    distance,
    style: {
      height: `${MOBILE_OPTION_HEIGHT}px`,
      lineHeight: `${MOBILE_OPTION_HEIGHT}px`,
      fontSize: `${fontSize}px`,
      fontWeight: 400,
      color: `rgb(${textShade}, ${textShade}, ${textShade})`,
      opacity,
      transform: `perspective(1400px) translate3d(0, ${translateY}px, ${translateZ}px) rotateX(${rotation}deg) scale(${scale}, ${scaleY})`,
      transformOrigin: 'center center',
      filter: `blur(${blur}px)`,
      willChange: 'transform, opacity, filter',
    },
  };
}
